<template>
    <Head title="Chat" />
    <Teleport to="body">
        <div class="safe-area-top-min fixed top-0 inset-x-0 px-3 flex items-center justify-between md:hidden z-30 pointer-events-none">
            <button
                type="button"
                :aria-label="is_development_journeys_chat ? `Back to ${development_journeys_return_label}` : 'Go back'"
                class="pointer-events-auto flex items-center gap-1 rounded-full bg-white/90 hover:bg-white px-3 py-1.5 shadow-sm border border-gray-200 text-valence-grey-750"
                data-testid="chat-detail-back-button-mobile"
                @click="handleGoBack">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">{{ is_development_journeys_chat ? development_journeys_return_label : "Back" }}</span>
            </button>
            <BaseButton
                v-if="!shouldShowSidebarControls && showWrapUp && !isBoxedPmChat"
                theme="primary"
                class="pointer-events-auto whitespace-nowrap animate-shadow-pulse"
                data-testid="chat-wrap-up-button-mobile"
                @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                Wrap Up
            </BaseButton>
        </div>
    </Teleport>
    <PromptVersionTestBanner
        v-if="internal_version_test_banner"
        :is-published-version="internal_version_test_banner.is_published_version"
        :settings-url="internal_version_test_banner.settings_url"
        :call-code="internal_version_test_banner.call_code"
        :internal-name="internal_version_test_banner.internal_name"
        :version-label="internal_version_test_banner.version_label"
        :entered-via-entrance="internal_version_test_banner.entered_via_entrance" />
    <div
        class="relative flex flex-col md:flex-row grow overflow-hidden max-h-full text-valence-grey-800"
        :class="isBoxedPmChat ? 'bg-valence-page md:gap-5 md:px-6 md:py-5 md:justify-center' : 'bg-[#FDFDFB]'">
        <div id="chat-container" class="relative grow flex flex-col overflow-hidden max-h-full" :class="{ 'md:max-w-(--breakpoint-md)': isBoxedPmChat }">
            <DevTools v-if="devtoolsEnabled" :messages="messages" :send-message="sendMessage" :prompt-id="first_prompt_id" :coaching-session-id="cs_id" />
            <ChatWrapUpDialog
                v-model:open="isChatWrapUpDialogOpen"
                :messages="messages"
                :user-last-name="userDetails.last_name"
                :user-first-name="userDetails.first_name"
                :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                :enable-follow-up="userDetails.can_receive_emails"
                :session-feedback="session_feedback"
                :is-performance-review-session="is_performance_review_session"
                @close="handleCloseChatWrapUpDialog" />
            <BaseDialog v-model:open="isEmailSummaryDialogOpen" title="Email Summary" show-close dialog-content-class="md:!max-w-2xl">
                <ChatSummary :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails" />
            </BaseDialog>
            <ThoughtProcessModal :messages="messages" />
            <!-- Boxed PM chats put Wrap Up in the card's title row instead of floating it over the stream. -->
            <div v-if="!shouldShowSidebarControls && showWrapUp && !isBoxedPmChat" class="hidden md:block absolute top-10 right-7 z-10">
                <BaseButton theme="primary" class="whitespace-nowrap animate-shadow-pulse" data-testid="chat-wrap-up-button" @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                    Wrap Up
                </BaseButton>
            </div>
            <!-- Mobile Controls and Widget List (RHS/Nadia Notes) -->
            <div
                v-if="shouldShowSidebarControls"
                :class="{ 'grow flex h-full': showWidgetSidebar }"
                class="shrink-0 md:hidden flex flex-col justify-end overflow-hidden max-h-full">
                <Teleport to="body">
                    <div v-if="!showWidgetSidebar" class="safe-area-top-min fixed top-0 right-0 -translate-x-6 items-center gap-4 md:hidden block">
                        <div class="flex justify-end">
                            <BaseButton
                                v-if="showEndRolePlay"
                                :disabled="!isEndRolePlayEnabled"
                                theme="primary"
                                class="body-small"
                                data-testid="role-play-recap-button-mobile"
                                @click="emitter.emit('end_role_play')">
                                Create Role Play Recap
                            </BaseButton>
                            <ChatWidgetListToggle :is-open="false" :visible-widget-count="visibleWidgetCount" @click="showWidgetSidebar = true" />
                        </div>
                        <ChatCalendarEvent
                            v-if="calendar_event"
                            class="mt-2"
                            :start-time="calendar_event.start_time"
                            :end-time="calendar_event.end_time"
                            :attendees="calendar_event.attendees"
                            :subject="calendar_event.subject" />
                    </div>
                </Teleport>
                <div v-show="showWidgetSidebar" class="overflow-y-hidden bg-white grow z-10">
                    <ChatWidgetList
                        :visible-widget-count="visibleWidgetCount"
                        :messages="messages"
                        :show-wrap-up="showWrapUp"
                        :show-end-role-play="showEndRolePlay"
                        :is-end-role-play-enabled="isEndRolePlayEnabled"
                        :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                        :nadia-notes-enabled="nadia_notes_enabled"
                        @close="showWidgetSidebar = false" />
                </div>
            </div>
            <!-- Boxed PM chat: the message stream lives in a bordered card with the review title
                 as its header row; `contents` keeps the default layout untouched otherwise. -->
            <div :class="isBoxedPmChat ? 'relative grow flex flex-col overflow-hidden min-h-0 bg-white md:rounded-2xl md:border md:border-valence-cream-200 md:mb-3' : 'contents'">
                <div v-if="isBoxedPmChat" class="relative shrink-0 px-6 pt-5" data-testid="pm-chat-title">
                    <div class="flex items-center gap-3">
                        <!-- Mobile: clear the floating Back pill (fixed top-left, ~90px wide incl. inset). -->
                        <h1 class="grow min-w-0 body text-valence-stone-900 truncate max-md:pl-20">{{ pmChatTitle }}</h1>
                        <BaseButton
                            v-if="showWrapUp"
                            theme="primary"
                            size="sm"
                            class="shrink-0 -my-1.5 whitespace-nowrap"
                            data-testid="chat-wrap-up-button-header"
                            @click="emitter.emit(CHAT_EVENT.WRAP_UP_CHAT)">
                            Wrap Up
                        </BaseButton>
                        <button
                            type="button"
                            class="md:hidden relative z-30 shrink-0 -my-1.5 -mr-2 flex h-9 w-9 items-center justify-center rounded-md text-valence-stone-900 transition-colors hover:bg-valence-cream-50"
                            :aria-expanded="showMobileTracker"
                            aria-label="Performance review navigation"
                            data-testid="pm-mobile-tracker-toggle"
                            @click="showMobileTracker = !showMobileTracker">
                            <i class="bi text-xl/none" :class="showMobileTracker ? 'bi-x-lg' : 'bi-list'" aria-hidden="true"></i>
                        </button>
                    </div>
                    <hr class="mt-4 border-valence-cream-200" />
                    <!-- Mobile only: the cycle tracker drops down from the title bar; on md+ it lives in the right-hand aside. -->
                    <template v-if="showMobileTracker">
                        <div class="md:hidden fixed inset-0 z-20" aria-hidden="true" @click="showMobileTracker = false"></div>
                        <div class="md:hidden absolute top-full inset-x-0 z-30 px-3 pt-1" data-testid="pm-mobile-tracker">
                            <PerformanceCycleTracker :context="performance_review_context" class="max-h-[65dvh] shadow-lg" />
                        </div>
                    </template>
                </div>
                <div v-if="is_development_journeys_chat" class="hidden w-full shrink-0 md:mx-auto md:flex md:max-w-(--breakpoint-md) md:px-4 md:pt-4">
                    <button
                        type="button"
                        class="body-small-semibold inline-flex items-center gap-2 text-valence-primary-700 hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-valence-primary-500"
                        data-testid="development-journeys-back-button"
                        @click="returnToDevelopmentJourneys">
                        <i class="bi bi-arrow-left" aria-hidden="true"></i>
                        Back to {{ development_journeys_return_label }}
                    </button>
                </div>
                <ChatMessageList
                    ref="chatMessageList"
                    :messages="messages"
                    :tool-call-status-text="toolCallStatusText"
                    :number-of-messages-at-init="numberOfMessagesAtInit"
                    :show-failed-response="show_failed_response"
                    :show-start-loading-message="showStartLoadingMessage"
                    :show-slow-response="show_slow_response"
                    :show-overflow="!sidebarIsTransitioning"
                    :show-widget-sidebar="showWidgetSidebar"
                    :waiting="waiting_for_assistant"
                    :is-waiting-for-initial-response="is_waiting_for_initial_response"
                    :is-thinking="is_thinking"
                    :user-first-name="userDetails.first_name"
                    :user-last-name="userDetails.last_name"
                    :has-activated-coaching-session="has_activated_coaching_session"
                    :coaching-session-created-at="coaching_session_created_at"
                    :show-header="!showEndRolePlay && !performance_review_context"
                    :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                    :edit-and-stop-generating-enabled="edit_and_stop_generating_enabled"
                    :boxed="isBoxedPmChat"
                    :my-work-in-chat-insight="my_work_in_chat_insight"
                    :perspective-new-chat-offer="activePerspectiveOffer"
                    :proactive-coaching-offer-enabled="talent_moment_proactive_offer_enabled"
                    @dismiss-failure="show_failed_response = false"
                    @perspective-offer-dismissed="perspectiveOfferDismissed = true"
                    @in-chat-role-play-started="handleInChatRolePlayStarted"
                    @role-play-canceled="handleInChatRolePlayCanceled">
                    <template #tool-result="{ line, message, lineIdx }">
                        <div
                            v-if="is_development_journeys_chat && isActionOfType(line, [ACTION.SET_UP_DEVELOPMENT_GOAL])"
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            data-testid="development-goal-review-announcement">
                            <DevelopmentGoalLockIn
                                v-if="!isDevelopmentGoalLockedIn(message, lineIdx)"
                                :goal="line.action_params.development_goal"
                                :coaching-session-uuid="cs_uuid" />
                            <DevelopmentGoalReview
                                v-if="isDevelopmentGoalLockedIn(message, lineIdx)"
                                :goal="line.action_params.development_goal"
                                :can-save="line === latestDevelopmentGoalAction?.line && !waiting_for_assistant"
                                :is-superseded="line !== latestDevelopmentGoalAction?.line"
                                :is-saved="isDevelopmentGoalSaved(line.action_params.development_goal)"
                                :is-saving="isSavingDevelopmentGoal(line.action_params.development_goal)"
                                action-label="Go to Development Journeys"
                                @save="saveDevelopmentGoal(line.action_params.development_goal)" />
                        </div>
                    </template>
                </ChatMessageList>
            </div>
            <DevelopmentGoalLockInActions
                v-if="is_development_journeys_chat && activeDevelopmentGoalLockIn"
                :disabled="waiting_for_assistant"
                data-testid="development-goal-lock-in-actions"
                @keep-editing="keepEditingDevelopmentGoal(activeDevelopmentGoalLockIn.message, activeDevelopmentGoalLockIn.lineIdx)"
                @lock-in="lockInDevelopmentGoal(activeDevelopmentGoalLockIn.message, activeDevelopmentGoalLockIn.lineIdx)" />
            <div class="w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2">
                <ChatNotification v-if="chatError" :message="chatError" theme="error" data-testid="chat-error" @dismiss="chatError = null" />
                <ChatNotification v-if="chatSuccess" :message="chatSuccess" theme="success" @dismiss="chatSuccess = null" />
                <ChatNotification v-else-if="show_failed_response" theme="error" data-testid="chat-failed-response" @dismiss="show_failed_response = false">
                    <p class="body-small-semibold">
                        Something went wrong. Please <button type="button" class="underline" @click="handleFailedResponseReload">refresh our chat.</button>
                    </p>
                </ChatNotification>
                <BaseNotification v-if="fileUploadLimitBanner" theme="error" @dismiss="fileUploadLimitBanner = false">
                    <template #content>
                        <p class="body-small-semibold">
                            You've reached the maximum number of files allowed in this conversation ({{ FILE_UPLOAD_LIMIT }}). Please
                            <button type="button" class="underline" @click="openUrl($startChatUrl, { queryParams: { chat_source: CHATSOURCE.FILE_UPLOAD_LIMIT } })">
                                start a new chat
                            </button>
                            before uploading new ones.
                        </p>
                    </template>
                </BaseNotification>
            </div>
            <Transition v-if="!hasErrorBanner && !activePerspectiveOffer" name="slide-up" mode="out-in">
                <div v-if="voiceNudgeAction" class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2">
                    <ChatVoiceNudge :content="voiceNudgeAction.line.action_params.content" @dismiss="dismissVoiceNudge" @activate-voice="activateVoiceFromNudge" />
                </div>
                <MSTeamsChatBanner
                    v-else
                    :org-has-ms-teams="org_has_ms_teams && teams_nudges_enabled"
                    :ms-teams-app-url="ms_teams_app_url"
                    :user-message-count="userMessageCount"
                    :coaching-session-id="cs_id"
                    :is-suppressed="is_ms_teams_chat_banner_suppressed" />
            </Transition>
            <div class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2">
                <PreferredLanguageNotice />
            </div>
            <ReturnToChatControls v-if="show_return_to_chat_controls && !returnToChatChoiceMade" @continue-chat="handleContinueChat" />
            <div :class="isBoxedPmChat ? 'w-full mb-2 px-2 md:px-0' : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto mb-2 lg:mb-6 px-2'">
                <ChatDraftManagerReviewButton
                    v-if="
                        performance_review_context &&
                        performance_review_context.workday_push_enabled &&
                        !performance_review_context.is_self &&
                        performance_review_context.workday_review_configured &&
                        latestMessageId
                    "
                    :latest-message-id="latestMessageId" />
                <ChatDraftSelfReviewButton v-if="showSelfReviewButton" :latest-message-id="latestMessageId" />
                <ChatGoalPushButton v-if="showGoalPushButton" :latest-message-id="latestMessageId" />
                <ChatMessageControls
                    v-if="!rolePlaySetupActive"
                    textarea-max-length="5000"
                    :location="CHAT_MESSAGE_CONTROLS_LOCATION.CHAT_DETAIL"
                    :is-sending="isBufferingMessage"
                    :is-transcribing="waiting_for_transcript"
                    :is-disabled="showStartLoadingMessage || isDevelopmentGoalChoicePending"
                    :show-slow-transcription="show_slow_transcription"
                    :default-text-input="defaultTextInput"
                    :user-message-count="messages.filter((m) => m.role === 'user').length"
                    :document-upload-enabled="document_upload_enabled"
                    :better-voice-composer-enabled="better_voice_composer_enabled"
                    :is-file-upload-limit="isFileUploadLimit"
                    :user-coach-exchange-id="userCoachExchangeId"
                    :pii-patterns="pii_patterns"
                    :waiting-for-assistant="waiting_for_assistant"
                    :edit-and-stop-generating-enabled="edit_and_stop_generating_enabled"
                    :has-workiq-connected="has_workiq_connected"
                    :placeholder="is_development_journeys_chat ? 'Ask Nadia to modify this goal…' : undefined"
                    @recording-complete="handleRecordingComplete"
                    @recording-error="handleRecordingError"
                    @recording-start="handleRecordingStart"
                    @textbox-show="handleTextboxShown"
                    @text-keydown="handleKeyDown"
                    @text-send="handleSendMessage"
                    @stop-generating="handleStopGenerating"
                    @upload-error="(errorMsg) => (chatError = errorMsg)" />
            </div>
            <div class="flex justify-center mb-2" :class="isBoxedPmChat ? 'mt-1' : 'lg:-mt-4'">
                <Disclaimer :message="disclaimer_message" />
            </div>
        </div>
        <aside v-if="shouldShowSidebarControls" class="hidden md:flex h-full overflow-hidden" :class="{ 'min-w-81.25': showWidgetSidebar }">
            <ChatWidgetSidebar
                :is-transitioning="sidebarIsTransitioning"
                :visible-widget-count="visibleWidgetCount"
                :role-play-adjustments="role_play_adjustments"
                :role-play-adjustment-default="role_play_adjustment_default"
                :show-widget-list="showWidgetSidebar"
                :show-wrap-up="showWrapUp"
                :show-end-role-play="showEndRolePlay"
                :is-end-role-play-enabled="isEndRolePlayEnabled"
                :messages="messages"
                :has-feedback-report="hasReport"
                :calendar-event="calendar_event"
                :hide-email-button="is_dogfood_instance || !userDetails.can_receive_emails"
                :nadia-notes-enabled="nadia_notes_enabled"
                @transition-start="sidebarIsTransitioning = true"
                @transition-end="sidebarIsTransitioning = false"
                @toggle-show-widget-list="handleToggleWidgetSidebar" />
        </aside>
        <aside v-else-if="isBoxedPmChat && performance_review_context" class="hidden md:flex w-80 shrink-0 flex-col overflow-hidden max-h-full">
            <PerformanceCycleTracker :context="performance_review_context" />
        </aside>
    </div>
    <CookieBanner />
    <BaseToast v-model:open="isChatToastOpen" :duration="CHAT_TOAST_DURATION_MS" :truncate-length="CHAT_TOAST_MAX_LENGTH" :description="chatToastMessage" />
</template>

<script>
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";

export default {
    layout: LeftSidebarLayout,
};
</script>

<script setup>
/**
 * Mirrors the `_PromptVersionTestBanner` dataclass in chat/views.py. Present only for a staff
 * prompt-version test chat; null otherwise (the banner renders iff this is non-null).
 * @typedef {object} PromptVersionTestBanner
 * @property {boolean} is_published_version
 * @property {string} settings_url
 * @property {string} call_code
 * @property {string} internal_name
 * @property {string} version_label
 * @property {boolean} entered_via_entrance
 */

import { convertToDateTime } from "/js/dateUtils";
import { getCookie } from "/js/utils.js";
import { Head, router } from "@inertiajs/vue3";
import { useEventListener } from "@vueuse/core";
import { fetchApi } from "~vue/api";
import { ACTION, getNotesCount, isActionOfType, messageHasActions, pluckAction, pluckMultipleActions } from "~vue/chatActions.js";
import ChatCalendarEvent from "~vue/ChatDetail/ChatCalendarEvent.vue";
import ChatDraftManagerReviewButton from "~vue/ChatDetail/ChatDraftManagerReviewButton.vue";
import ChatDraftSelfReviewButton from "~vue/ChatDetail/ChatDraftSelfReviewButton.vue";
import ChatGoalPushButton from "~vue/ChatDetail/ChatGoalPushButton.vue";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import ChatSummary from "~vue/ChatDetail/ChatSummary.vue";
import ChatVoiceNudge from "~vue/ChatDetail/ChatVoiceNudge.vue";
import ChatWrapUpDialog from "~vue/ChatDetail/ChatWrapUpDialog.vue";
import DevelopmentGoalLockIn from "~vue/ChatDetail/DevelopmentGoalLockIn.vue";
import DevelopmentGoalLockInActions from "~vue/ChatDetail/DevelopmentGoalLockInActions.vue";
import DevelopmentGoalReview from "~vue/ChatDetail/DevelopmentGoalReview.vue";
import PerformanceCycleTracker from "~vue/ChatDetail/PerformanceCycleTracker.vue";
import ChatMessageList from "~vue/ChatMessageList.vue";
import ChatNotification from "~vue/ChatNotification.vue";
import ChatWidgetList from "~vue/ChatWidgetList.vue";
import ChatWidgetListToggle from "~vue/ChatWidgetListToggle.vue";
import ChatWidgetSidebar from "~vue/ChatWidgetSidebar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import MSTeamsChatBanner from "~vue/components/MSTeamsChatBanner.vue";
import PreferredLanguageNotice from "~vue/components/PreferredLanguageNotice.vue";
import PromptVersionTestBanner from "~vue/components/PromptVersionTestBanner.vue";
import ReturnToChatControls from "~vue/components/ReturnToChatControls.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { useDevelopmentGoalSave } from "~vue/composables/useDevelopmentGoalSave";
import { useKeyboard } from "~vue/composables/useKeyboard";
import { useLeaveConfirmation } from "~vue/composables/useLeaveConfirmation";
import { useSendStreamingEvent } from "~vue/composables/useSendStreamingEvent";
import { useStatusBar } from "~vue/composables/useStatusBar";
import DevTools from "~vue/DevTools.vue";
import Disclaimer from "~vue/Disclaimer.vue";
import { CHAT_EVENT } from "~vue/events";
import { createRealtimeClient } from "~vue/RealtimeClientFactory.js";
import { useChatStore } from "~vue/stores/chatStore";
import ThoughtProcessModal from "~vue/ThoughtProcessModal.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION, CHATSOURCE, FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { createWebSocketErrorHandler, logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getWafErrorMessage, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import { computed, inject, nextTick, onMounted, onUnmounted, provide, reactive, ref, useTemplateRef, watch } from "vue";

import { transcribeRecording } from "../../transcription.js";
import { getRandomInt } from "../utils/random.js";

useStatusBar();

const userDetails = inject("user_details");
const organizationDetails = inject("organization_details");
const { emitter, $setEventContext, $sendEvent, $sendSocketEvent, $feedbackMailto } = inject("globalProperties");

const ONBOARDING_TASK = {
    ROLE: "action_onboarding_role",
    MIC: "action_onboarding_mic",
    FOLLOWUP: "action_onboarding_followup",
    GUIDANCE: "action_onboarding_guidance",
    BOOKMARK: "action_onboarding_bookmark",
};

const SLOW_MESSAGE_TIMEOUT_MS = 2000;
const FAILED_MESSAGE_TIMEOUT_MS = 55000;

const CHAT_ERROR = {
    TRANSCRIPTION_EXHAUSTED: "We're having issues transcribing your audio message. Please try again later.",
    RECORDING_DEFAULT: "We're having issues recording your audio message. Please try again later.",
    SEND_FAILURE: "We're having issues sending your message. Please try again later.",
    NO_SPEECH_DETECTED: "I didn't quite catch that. Please try again.",
};

const AUTO_OPEN_TEXT_INPUT_ERRORS = [CHAT_ERROR.TRANSCRIPTION_EXHAUSTED, CHAT_ERROR.RECORDING_DEFAULT, CHAT_ERROR.SEND_FAILURE];

const {
    is_development_journeys_chat,
    development_journeys_url,
    development_journeys_return_label,
    role_play_adjustment_default,
    role_play_adjustments,
    event_context,
    stream_url,
    is_start_loading_message_enabled,
    disclaimer_message,
    cs_id,
    cs_uuid,
    chat_source,
    coaching_session_created_at,
    first_prompt_id,
    dev_tools_enabled,
    document_upload_enabled,
    role_play: initialRolePlay,
    feedback_report,
    connections,
    calendar_event,
    is_dogfood_instance,
    show_return_to_chat_controls,
    session_feedback,
    org_has_ms_teams,
    ms_teams_app_url,
    teams_nudges_enabled,
    public_profile_enabled,
    collaboration_profile_invite_url,
    is_ms_teams_chat_banner_suppressed,
    pii_patterns,
    memory_connections_frontend_driven_enabled,
    better_voice_composer_enabled,
    nadia_notes_enabled,
    edit_and_stop_generating_enabled,
    is_performance_review_session,
    performance_review_context,
    in_chat_pm_enabled,
    internal_version_test_banner,
    has_workiq_connected,
    my_work_in_chat_insight,
    perspective_new_chat_offer,
    talent_moment_proactive_offer_enabled,
} = defineProps({
    is_development_journeys_chat: { type: Boolean, default: false },
    development_journeys_url: { type: String, default: "" },
    development_journeys_return_label: { type: String, default: "Development Journeys" },
    role_play_adjustment_default: { type: String, default: "" },
    role_play_adjustments: { type: Array, default: () => [] },
    event_context: { type: Object, default: () => ({}) },
    stream_url: { type: String, default: "" },
    is_start_loading_message_enabled: { type: Boolean, default: false },
    disclaimer_message: { type: String, default: "" },
    cs_id: { type: String, default: "" },
    cs_uuid: { type: String, default: null },
    chat_source: { type: String, default: null },
    coaching_session_created_at: { type: String, required: true },
    first_prompt_id: { type: Number, required: true },
    dev_tools_enabled: { type: Boolean },
    has_activated_coaching_session: { type: Boolean, default: false },
    default_chat_redirect_url: { type: String, default: "/" },
    document_upload_enabled: { type: Boolean, default: false },
    role_play: { type: Object, default: undefined },
    feedback_report: { type: Object, default: () => ({}) },
    connections: { type: Array, default: () => [] },
    calendar_event: { type: Object, default: undefined },
    is_dogfood_instance: { type: Boolean, default: false },
    show_return_to_chat_controls: { type: Boolean, default: false },
    session_feedback: { type: Object, default: null },
    org_has_ms_teams: { type: Boolean, default: false },
    ms_teams_app_url: { type: String, default: "" },
    teams_nudges_enabled: { type: Boolean, default: false },
    public_profile_enabled: { type: Boolean, default: false },
    collaboration_profile_invite_url: { type: String, default: "" },
    is_ms_teams_chat_banner_suppressed: { type: Boolean, default: false },
    pii_patterns: { type: Array, default: null },
    memory_connections_frontend_driven_enabled: { type: Boolean, default: false },
    better_voice_composer_enabled: { type: Boolean, default: false },
    nadia_notes_enabled: { type: Boolean, default: false },
    edit_and_stop_generating_enabled: { type: Boolean, default: false },
    is_performance_review_session: { type: Boolean, default: false },
    performance_review_context: {
        /** @type {import('vue').PropType<import('~vue/types/performance_management').PerformanceReviewContext | null>} */
        type: Object,
        default: null,
    },
    in_chat_pm_enabled: { type: Boolean, default: false },
    internal_version_test_banner: {
        /** @type {import('vue').PropType<PromptVersionTestBanner | null>} */
        type: Object,
        default: null,
    },
    has_workiq_connected: { type: Boolean, default: false },
    my_work_in_chat_insight: {
        /** @type {import('vue').PropType<import('~vue/ChatDetail/types').MyWorkInChatInsight | null>} */
        type: Object,
        default: null,
    },
    perspective_new_chat_offer: {
        /** @type {import('vue').PropType<import('~vue/ChatDetail/types').PerspectiveNewChatOffer | null>} */
        type: Object,
        default: null,
    },
    talent_moment_proactive_offer_enabled: { type: Boolean, default: false },
});

provide("coachingSessionId", cs_id);
provide("coachingSessionUuid", cs_uuid);
provide(
    "connections",
    computed(() => connections),
);
provide("publicProfileEnabled", public_profile_enabled);
provide("collaborationProfileInviteUrl", collaboration_profile_invite_url);
provide("feedbackReport", feedback_report);

const CHAT_TOAST_DURATION_MS = 10000;
const CHAT_TOAST_MAX_LENGTH = 200;
const isChatToastOpen = ref(false);
const chatToastMessage = ref("");
const showChatToast = (message) => {
    chatToastMessage.value = message;
    isChatToastOpen.value = true;
};
provide("showChatToast", showChatToast);

const sendStreamingEvent = stream_url ? useSendStreamingEvent(stream_url, emitter, event_context) : null;
provide("sendStreamingEvent", sendStreamingEvent);

const messages = ref([]);
const lockedInDevelopmentGoalActions = ref(new Set());
const lockingInDevelopmentGoalActions = ref(new Set());
const dismissedDevelopmentGoalActions = ref(new Set());
const isDevelopmentGoalDraftMessage = (message) => messageHasActions(message, [ACTION.SET_UP_DEVELOPMENT_GOAL]);
const DEVELOPMENT_GOAL_CARD_ACTION = {
    LOCK_IN: "lock_in",
    KEEP_EDITING: "keep_editing",
};
const latestDevelopmentGoalAction = computed(() => {
    for (const message of [...messages.value].reverse()) {
        for (let lineIdx = (message.lines ?? []).length - 1; lineIdx >= 0; lineIdx -= 1) {
            const line = message.lines[lineIdx];
            if (line.type === "action" && line.action_name === ACTION.SET_UP_DEVELOPMENT_GOAL) {
                return { message, line, lineIdx };
            }
        }
    }

    return null;
});

const developmentGoalActionKey = (message, lineIdx) => {
    const messageKey = message.chat_message_id ?? message.message_id ?? message.id ?? messages.value.indexOf(message);

    return `${messageKey}:${lineIdx}`;
};

const developmentGoalsMatch = (left, right) =>
    left?.title === right?.title && left?.achievement_criteria === right?.achievement_criteria && left?.target_date === right?.target_date;

const hasPersistedDevelopmentGoalLockIn = (message, lineIdx) => {
    const sourceLine = message.lines[lineIdx];
    const sourceGoal = sourceLine?.action_params?.development_goal;

    return message.lines.some((line) => {
        if (line.type !== "action" || line.action_name !== ACTION.DEVELOPMENT_GOAL_LOCKED_IN) {
            return false;
        }

        const sourceActionIndex = line.action_params?.source_action_index;
        if (sourceActionIndex != null && sourceLine?.index != null) {
            return sourceActionIndex === sourceLine.index;
        }

        return developmentGoalsMatch(line.action_params?.development_goal, sourceGoal);
    });
};

const isDevelopmentGoalLockedIn = (message, lineIdx) =>
    lockedInDevelopmentGoalActions.value.has(developmentGoalActionKey(message, lineIdx)) || hasPersistedDevelopmentGoalLockIn(message, lineIdx);
const isDevelopmentGoalDismissed = (message, lineIdx) => dismissedDevelopmentGoalActions.value.has(developmentGoalActionKey(message, lineIdx));
const hasUserMessageAfterDevelopmentGoal = (message) => {
    const messageIdx = messages.value.indexOf(message);
    return messageIdx !== -1 && messages.value.slice(messageIdx + 1).some((laterMessage) => laterMessage.role === "user");
};

const dismissDevelopmentGoalLockIn = (message, lineIdx) => {
    dismissedDevelopmentGoalActions.value = new Set(dismissedDevelopmentGoalActions.value).add(developmentGoalActionKey(message, lineIdx));
};

const activeDevelopmentGoalLockIn = computed(() => {
    const latestAction = latestDevelopmentGoalAction.value;

    if (
        !latestAction ||
        isDevelopmentGoalLockedIn(latestAction.message, latestAction.lineIdx) ||
        isDevelopmentGoalDismissed(latestAction.message, latestAction.lineIdx) ||
        hasUserMessageAfterDevelopmentGoal(latestAction.message)
    ) {
        return null;
    }

    return latestAction;
});

const isDevelopmentGoalLockInPending = computed(() => is_development_journeys_chat && !!activeDevelopmentGoalLockIn.value);
const isDevelopmentGoalChoicePending = computed(() => isDevelopmentGoalLockInPending.value || (is_development_journeys_chat && lockingInDevelopmentGoalActions.value.size > 0));

// A draft goal or its in-flight lock-in has not been persisted yet, so leaving that phase silently loses the goal.
useLeaveConfirmation("You haven't locked in your goal yet. Are you sure you want to leave?", isDevelopmentGoalChoicePending);

const logDevelopmentGoalCardAction = (action) => {
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GOAL_CARD_ACTION, { development_journeys_goal_card_action: action }, cs_uuid);
};

const lockInDevelopmentGoal = async (message, lineIdx) => {
    const line = message.lines[lineIdx];
    if (line !== latestDevelopmentGoalAction.value?.line || waiting_for_assistant.value) {
        return;
    }

    logDevelopmentGoalCardAction(DEVELOPMENT_GOAL_CARD_ACTION.LOCK_IN);

    const actionKey = developmentGoalActionKey(message, lineIdx);
    lockingInDevelopmentGoalActions.value = new Set(lockingInDevelopmentGoalActions.value).add(actionKey);
    lockedInDevelopmentGoalActions.value = new Set(lockedInDevelopmentGoalActions.value).add(actionKey);

    const action = {
        chat_message_id: message.chat_message_id,
        type: ACTION.DEVELOPMENT_GOAL_LOCKED_IN,
        action_params: {
            development_goal: line.action_params.development_goal,
            source_action_index: line.index ?? lineIdx,
        },
        action_state: {
            dismissed: false,
            minimized: false,
            submitted: true,
        },
    };

    try {
        if (sendStreamingEvent) {
            const persisted = await sendStreamingEvent("create_action", action);
            if (!persisted) {
                throw new Error("The goal lock-in action was not saved.");
            }
        } else {
            await $sendEvent("create_action", action);
        }
    } catch {
        const lockedActions = new Set(lockedInDevelopmentGoalActions.value);
        lockedActions.delete(actionKey);
        lockedInDevelopmentGoalActions.value = lockedActions;
        chatError.value = "We couldn't lock in this goal. Please try again.";
    } finally {
        const lockingActions = new Set(lockingInDevelopmentGoalActions.value);
        lockingActions.delete(actionKey);
        lockingInDevelopmentGoalActions.value = lockingActions;
    }
};

const returnToDevelopmentJourneys = () => {
    if (development_journeys_url) {
        router.visit(development_journeys_url);
    }
};

// The manager-review draft button attaches the card to the newest message, so it lands at the bottom.
const latestMessageId = computed(() => messages.value[messages.value.length - 1]?.chat_message_id);
// Live progress text for in-flight tool calls, keyed by tool_call_id (see handleToolCallStatus).
// A reactive Map (not a ref) so ChatMessageList's `toolCalls` computed re-runs on individual
// key updates without needing to replace the whole Map on every status tick.
const toolCallStatusText = reactive(new Map());
const waiting_for_transcript = ref(false);
const waiting_for_assistant = ref(false);
const show_slow_response = ref(false);
const show_failed_response = ref(false);
const show_slow_transcription = ref(false);
const has_taken_action = ref(false);
const is_waiting_for_initial_response = ref(true);
const is_thinking = ref(false);
const slow_response_timeout = ref(null);
const failed_response_timeout = ref(null);
const backgrounded_at = ref(null);
const rolePlayAdjust = ref(null);
const showStartLoadingMessage = ref(is_start_loading_message_enabled);
const startLoadingMessageTimeout = ref(null);
const firstSocketMessageReceived = ref(false);
const numberOfMessagesAtInit = ref(0);
const showWidgetSidebar = ref(false);
const sidebarIsTransitioning = ref(false);
const chatError = ref(null);
const chatSuccess = ref(null);
const promptSwitchCallCode = ref(null);
const socketClient = ref(null);
const devtoolsEnabled = ref(dev_tools_enabled || import.meta.env.DEV);
const dictated_in_this_message = ref(false);
const isChatWrapUpDialogOpen = ref(false);
const isEmailSummaryDialogOpen = ref(false);
const isInitialized = ref(false);
const userCoachExchangeId = ref(crypto.randomUUID());
const returnToChatChoiceMade = ref(false);

const {
    isSaving: isSavingDevelopmentGoal,
    isSaved: isDevelopmentGoalSaved,
    saveDevelopmentGoal,
} = useDevelopmentGoalSave({
    coachingSessionUuid: cs_uuid,
    developmentJourneysUrl: development_journeys_url,
    setError: (message) => {
        chatError.value = message;
    },
});

const resetAssistantResponseState = () => {
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;
    clearSlowResponseTimeout();
};

// RAF throttling for streaming updates to reduce CPU overhead
let partialUpdateRafId = null;

const fileUploadLimitBanner = ref(false);
const hasErrorBanner = computed(() => !!chatError.value || show_failed_response.value || fileUploadLimitBanner.value);
const isFileUploadLimit = computed(() => {
    const fileUploadActions = pluckMultipleActions(messages.value, ACTION.FILE_UPLOAD);

    const fileUploadCount = fileUploadActions.reduce((acc, action) => acc + action.line.action_params.length, 0);
    return fileUploadCount >= FILE_UPLOAD_LIMIT;
});

const rolePlaySetupActive = computed(() => {
    const rolePlaySetupActions = pluckMultipleActions(messages.value, ACTION.ROLE_PLAY_SETUP);
    if (!rolePlaySetupActions.length) {
        return false;
    }

    const latestSetupAction = rolePlaySetupActions[rolePlaySetupActions.length - 1];
    if (latestSetupAction.line.action_state.submitted || latestSetupAction.line.action_state.dismissed) {
        return false;
    }

    const setupMessageIndex = messages.value.findIndex((message) => message.chat_message_id === latestSetupAction.message.chat_message_id);
    const laterMessages = messages.value.slice(setupMessageIndex + 1);
    const hasLaterConversationContent = laterMessages.some((message) =>
        message.lines.some(
            (line) => (line.type === "md" && !line.hidden && line.content.trim().length > 0) || (line.type === "action" && line.action_name === ACTION.ROLE_PLAY_SUMMARY),
        ),
    );

    return !hasLaterConversationContent;
});

watch(
    isFileUploadLimit,
    (newVal) => {
        if (newVal) {
            fileUploadLimitBanner.value = true;
            logUserInteraction("file_upload_limit_reached", {}, cs_uuid);
        }
    },
    { immediate: true },
);

const showWrapUp = computed(() => messages.value.filter((m) => m.role === "user").length >= 2 && !useChatStore.rolePlay?.hashid);

const showEndRolePlay = computed(() => !!useChatStore.rolePlay?.hashid && !useChatStore.rolePlay?.finished_at);
const isEndRolePlayEnabled = computed(() => {
    const rolePlayMonitorActions = pluckMultipleActions(messages.value, ACTION.ROLE_PLAY_MONITOR);
    return rolePlayMonitorActions.filter((action) => action.line.action_params.role_play_hashid === useChatStore.rolePlay.hashid).length > 1;
});

const visibleWidgetCount = computed(() => {
    return getNotesCount(messages.value);
});

const hasRolePlayContent = computed(() => {
    const rolePlay = useChatStore.rolePlay;
    // Use `hashid` (not `id`) to detect an active role play: on page-load hydration the
    // role play is serialized via RolePlay.to_dict() → model_to_dict(), which drops the
    // non-editable integer `id` PK but keeps `hashid`. Keying off `id` here made the sidebar
    // (and the recap button inside it) unmount on reload during an active role play (BUG-627).
    const hasActiveRolePlay = rolePlay?.finished_at == undefined && rolePlay?.hashid;
    const hasRolePlayMonitor = pluckAction(messages.value, ACTION.ROLE_PLAY_MONITOR);
    return !!hasActiveRolePlay || !!hasRolePlayMonitor;
});

// Dedicated PM chats never show the right-hand widget sidebar; the cycle tracker carries the where-am-I context instead.
const shouldShowSidebarControls = computed(() => (nadia_notes_enabled || hasRolePlayContent.value) && !(is_performance_review_session && in_chat_pm_enabled));

// Boxed PM layout: messages in a bordered card with a title row, cycle tracker on the right.
const isBoxedPmChat = computed(() => is_performance_review_session && in_chat_pm_enabled && !!performance_review_context);
// Only on a *self* goal-setting moment (not a self review), and once there's a message to anchor the action to.
const showGoalPushButton = computed(
    () =>
        performance_review_context?.workday_push_enabled === true &&
        performance_review_context?.is_self === true &&
        performance_review_context?.moment_type === "goal_setting" &&
        latestMessageId.value != null,
);
// The self-review counterpart of the manager-review button: an end-of-year *self* review, once
// there's a message to anchor the action to. Push availability is re-checked server-side.
const showSelfReviewButton = computed(
    () =>
        performance_review_context?.workday_push_enabled === true &&
        performance_review_context?.is_self === true &&
        performance_review_context?.moment_type === "end_of_year" &&
        latestMessageId.value != null,
);
// Mobile: the tracker has no room beside the chat, so it drops down from a hamburger in the title bar.
const showMobileTracker = ref(false);
// The cycle (moment) name — which review period you're in. The specific task and person
// are the tracker's job: it highlights the row for this very chat.
const pmChatTitle = computed(() => performance_review_context?.cycle ?? "");

const userMessageCount = computed(() => {
    return messages.value.filter((m) => m.role === "user").length;
});

// While the Perspective offer is up it owns the banner slot: the voice nudge and Teams banner wait until it is dismissed.
const perspectiveOfferDismissed = ref(false);
const activePerspectiveOffer = computed(() => (perspectiveOfferDismissed.value ? null : perspective_new_chat_offer));

const voiceNudgeDismissed = ref(false);
const voiceNudgeAction = computed(() => {
    // Suppresses nudges already persisted on past messages; new ones are blocked server-side
    // in chat.widget_tools.voice_nudge.
    if (voiceNudgeDismissed.value || organizationDetails?.value?.is_chat_voice_input_hidden) {
        return null;
    }
    for (const message of messages.value) {
        for (const line of message.lines) {
            if (line.type === "action" && line.action_name === ACTION.VOICE_NUDGE && !line.action_state?.dismissed) {
                return { line, lineIdx: message.lines.indexOf(line), message };
            }
        }
    }
    return null;
});

function dismissVoiceNudge() {
    if (!voiceNudgeAction.value) {
        return;
    }
    const { message, line } = voiceNudgeAction.value;
    voiceNudgeDismissed.value = true;
    emitter.emit("voice_nudge", {
        message_id: message.chat_message_id,
        lineIdx: message.lines.indexOf(line),
        action_state: { dismissed: true },
    });
    logUserInteraction(AmplitudeEventNames.VOICE_NUDGE_DISMISSED, {}, cs_uuid);
}

function activateVoiceFromNudge() {
    emitter.emit(CHAT_EVENT.OPEN_VOICE_INPUT);
    if (voiceNudgeAction.value) {
        const { message, line } = voiceNudgeAction.value;
        voiceNudgeDismissed.value = true;
        emitter.emit("voice_nudge", {
            message_id: message.chat_message_id,
            lineIdx: message.lines.indexOf(line),
            action_state: { dismissed: true },
        });
    }
    logUserInteraction(AmplitudeEventNames.VOICE_NUDGE_MIC_ACTIVATED, {}, cs_uuid);
}

// Use stored user preference, fallback to text input (winning experiment result)
const userChatInputPreference = useChatInputPreference();
const defaultTextInput = computed(() => {
    return userChatInputPreference.value !== null ? userChatInputPreference.value : true;
});

watch(chatError, (current, old) => {
    if (current && !old && AUTO_OPEN_TEXT_INPUT_ERRORS.includes(current)) {
        emitter.emit(CHAT_EVENT.OPEN_TEXT_INPUT);
    }
});

const cycleUserCoachExchangeId = () => {
    userCoachExchangeId.value = crypto.randomUUID();
    return userCoachExchangeId.value;
};

onMounted(() => {
    $setEventContext(event_context);
    if (stream_url) {
        const cid = new URLSearchParams(window.location.search).get("cid");
        socketClient.value = createRealtimeClient(stream_url, {
            reconnectEnabled: true,
            reconnectInterval: 2000,
        });
        socketClient.value.connect();
        let firstMessageReceived = false;

        socketClient.value.onMessage = (msg) => {
            const message_data = JSON.parse(msg.data);
            if (!firstMessageReceived) {
                firstMessageReceived = true;
                emitter.emit("first_socket_message");
            }
            if (message_data.type) {
                emitter.emit(message_data.type, message_data);
            }
        };
        socketClient.value.onOpen = () => {
            if (!isInitialized.value) {
                if (cid) {
                    socketClient.value.nextCorrelationId = cid;
                }
                $sendSocketEvent(
                    "initialize_chat",
                    {
                        dictation_on: useChatStore.dictationOn,
                        use_workiq: useChatStore.useWorkiq,
                        uce_id: cycleUserCoachExchangeId(),
                    },
                    socketClient.value,
                );
            }
        };
        socketClient.value.onClose = (closeEvent) => {
            resetAssistantResponseState();
            if (closeEvent.code === 4401) {
                chatError.value = "You've been logged out. Please log in again.";
            } else {
                chatError.value = "Trouble connecting to the chat. Please refresh and try again.";
            }
        };
        socketClient.value.onError = createWebSocketErrorHandler({ cs_id, userCoachExchangeId }, "ChatDetail", (e) => {
            resetAssistantResponseState();
            // The session-ended dialog is already up and says the same thing, only accurately --
            // it knows whether the user needs a reload or a fresh login (ENG-1667).
            if (isSessionEndedError(e)) {
                return;
            }
            if (e?.cause === WAF_ERROR_CAUSE) {
                chatError.value = getWafErrorMessage($feedbackMailto);
            } else if (e?.status === 403) {
                chatError.value = "Your session is out of date. Please reload the app and log in again.";
            } else {
                chatError.value = "Trouble connecting to the chat. Please refresh and try again.";
            }
        });
    }

    if (initialRolePlay) {
        useChatStore.setRolePlay(initialRolePlay);
    }

    logUserInteraction("first_paint", {}, cs_uuid);

    // NOTE: Every listener added here must have a corresponding "off" call in onUmounted().
    emitter.on("chat_message", handleChatMessage);
    emitter.on("chat_warning", handleChatWarning);
    emitter.on("chat_error", handleChatError);
    emitter.on("transcription_event", handleTranscriptionEvent);
    emitter.on("chat_partial_addition", handleChatPartialAddition);
    emitter.on("tool_call_status", handleToolCallStatus);
    emitter.on("initialize_chat", handleChatInit);
    emitter.on("choose_followup", handleChooseFollowup);
    emitter.on("followup_update", handleFollowupChange);
    emitter.on("profile_question", handleProfileQuestion);
    emitter.on(ACTION.ROLE_PLAY_ADJUST, handleRolePlayAdjust);
    emitter.on("action_items", handleActionItems);
    emitter.on("voice_nudge", handleVoiceNudge);
    emitter.on(ACTION.PM_PICKER, handlePmPicker);
    emitter.on("first_socket_message", handleFirstSocketMessage);
    emitter.on("onboarding_followup_scheduled", handleOnboardingFollowup);
    emitter.on(CHAT_EVENT.WRAP_UP_CHAT, handleWrapUpChat);
    emitter.on(CHAT_EVENT.OPEN_SESSION_SUMMARY, handleOpenEmailSummary);
    emitter.on("pii_rejected", handlePiiRejected);
    emitter.on("error_notification", handleErrorNotification);
    emitter.on("success_notification", handleSuccessNotification);
    emitter.on("role_play_started", handleRolePlayStarted);
    emitter.on(CHAT_EVENT.EDIT_MESSAGE, handleEditMessage);
    emitter.on(CHAT_EVENT.STOP_GENERATING, handleStopGenerating);
    emitter.on("end_role_play", handleEndRolePlay);
    emitter.on("initial_response_started", handleInitialResponseStarted);
    emitter.on("chat_send_deduplicated", handleChatSendDeduplicated);
    emitter.on("thinking_started", handleThinkingStarted);
    emitter.on("visible_content_started", handleVisibleContentStarted);
});

onUnmounted(() => {
    $setEventContext({});
    emitter.emit(CHAT_EVENT.LEAVING_CHAT);
    if (socketClient.value) {
        socketClient.value.removeListeners();
        socketClient.value.instance?.close();
    }

    // Cancel any pending RAF for streaming updates
    if (partialUpdateRafId) {
        cancelAnimationFrame(partialUpdateRafId);
        partialUpdateRafId = null;
    }

    useChatStore.setRolePlay({});

    emitter.off("chat_message", handleChatMessage);
    emitter.off("chat_warning", handleChatWarning);
    emitter.off("chat_error", handleChatError);
    emitter.off("transcription_event", handleTranscriptionEvent);
    emitter.off("chat_partial_addition", handleChatPartialAddition);
    emitter.off("tool_call_status", handleToolCallStatus);
    emitter.off("initialize_chat", handleChatInit);
    emitter.off("choose_followup", handleChooseFollowup);
    emitter.off("followup_update", handleFollowupChange);
    emitter.off("profile_question", handleProfileQuestion);
    emitter.off(ACTION.ROLE_PLAY_ADJUST, handleRolePlayAdjust);
    emitter.off("action_items", handleActionItems);
    emitter.off("voice_nudge", handleVoiceNudge);
    emitter.off(ACTION.PM_PICKER, handlePmPicker);
    emitter.off("first_socket_message", handleFirstSocketMessage);
    emitter.off("onboarding_followup_scheduled", handleOnboardingFollowup);
    emitter.off(CHAT_EVENT.WRAP_UP_CHAT, handleWrapUpChat);
    emitter.off(CHAT_EVENT.OPEN_SESSION_SUMMARY, handleOpenEmailSummary);
    emitter.off("pii_rejected", handlePiiRejected);
    emitter.off("error_notification", handleErrorNotification);
    emitter.off("success_notification", handleSuccessNotification);
    emitter.off("role_play_started", handleRolePlayStarted);
    emitter.off(CHAT_EVENT.EDIT_MESSAGE, handleEditMessage);
    emitter.off(CHAT_EVENT.STOP_GENERATING, handleStopGenerating);
    emitter.off("end_role_play", handleEndRolePlay);
    emitter.off("initial_response_started", handleInitialResponseStarted);
    emitter.off("chat_send_deduplicated", handleChatSendDeduplicated);
    emitter.off("thinking_started", handleThinkingStarted);
    emitter.off("visible_content_started", handleVisibleContentStarted);
});

const hasReport = computed(() => {
    return feedback_report && Object.keys(feedback_report).length > 0;
});

const clearLoadingMessageTimeout = () => {
    if (startLoadingMessageTimeout.value !== null) {
        window.clearTimeout(startLoadingMessageTimeout.value);
        startLoadingMessageTimeout.value = null;
    }
};

useEventListener(document, "visibilitychange", () => {
    const eventAt = new Date();
    const now = eventAt.toISOString();
    const eventName = document.visibilityState === "hidden" ? "backgrounded_tab" : "resumed_tab";
    const uce_id = userCoachExchangeId.value;

    let shouldReload = false;
    let minutesSpentBackgrounded = null;
    if (eventName === "backgrounded_tab") {
        backgrounded_at.value = eventAt;
    } else if (backgrounded_at.value) {
        minutesSpentBackgrounded = (eventAt - backgrounded_at.value) / 1000 / 60;
        shouldReload = minutesSpentBackgrounded > 8 * 60;
    }

    window?.Sentry?.addBreadcrumb({
        message: "ChatDetail received 'visibilitychange' event.",
        data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload },
    });

    new Promise((resolve, reject) => {
        logUserInteraction(eventName, { uce_id, now }, cs_uuid)
            .then(resolve)
            .catch((err) => {
                const message = `Failed to send '${eventName}' event.  Will wait a few seconds and retry.`;
                console.warn(message, err);
                window?.Sentry?.addBreadcrumb({
                    type: "error",
                    category: "error",
                    level: "warning",
                    message,
                    data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload, err },
                });
                setTimeout(() => {
                    logUserInteraction(eventName, { uce_id, now }, cs_uuid)
                        .then(resolve)
                        .catch((err) => {
                            const message = `Failed to send '${eventName}' event after a second try: ${err}`;
                            console.error(message);
                            window?.Sentry?.addBreadcrumb({
                                type: "error",
                                category: "error",
                                level: "error",
                                message,
                                data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded, shouldReload, err },
                            });
                            reject(new Error(message));
                        });
                }, 2000);
            });
    }).finally(() => {
        if (shouldReload) {
            window?.Sentry?.addBreadcrumb({
                message: "Reloading document.",
                data: { eventName, eventAt, uce_id, cs_id, minutesSpentBackgrounded },
            });
            document.location.reload();
        }
    });
});

useEventListener(window, "beforeunload", () => {
    logUserInteraction("chat_unload", {}, cs_uuid);
});

const handleFirstSocketMessage = () => {
    firstSocketMessageReceived.value = true;
    /*
     * Clear the loading message threshold in case we received a
     * message through the socket before the loading message timeout
     * runs. This avoids showing the loading message unnecessarily.
     */
    clearLoadingMessageTimeout();
};

const handleChatInit = (data) => {
    messages.value = data.data.messages;
    numberOfMessagesAtInit.value = data.data.messages.length;
    scrollToBottom();

    // If navigating from search results (?msg=<id>), scroll to the matched message
    const searchMsgId = Number(new URLSearchParams(window.location.search).get("msg"));
    if (searchMsgId && !isNaN(searchMsgId)) {
        nextTick(() => {
            emitter.emit(CHAT_EVENT.FOCUS_CHAT_MESSAGE, { chatMessageId: searchMsgId });
        });
    }

    // Initialize role play adjustment if last message has a
    // role_play_adjustment action.
    if (messages.value.length > 0) {
        const lastRolePlayAdjust = pluckAction([messages.value[messages.value.length - 1]], ACTION.ROLE_PLAY_ADJUST, true);
        if (lastRolePlayAdjust && lastRolePlayAdjust?.line.action_params?.adjustment) {
            rolePlayAdjust.value = lastRolePlayAdjust.line.action_params.adjustment;
        }
    }

    // For new conversations with no visible user messages, the backend will automatically
    // stream a first message. Set waiting state so the thinking indicators show.
    const visibleUserMessages = messages.value.filter((m) => m.role === "user" && !m.hidden);
    const assistantMessages = messages.value.filter((m) => m.role === "assistant");
    const hasDevelopmentGoalDraft = messages.value.some(isDevelopmentGoalDraftMessage);

    if (visibleUserMessages.length === 0 && assistantMessages.length === 0 && !hasDevelopmentGoalDraft) {
        // No visible user messages and no assistant messages yet - waiting for first response
        is_waiting_for_initial_response.value = true;
        waiting_for_assistant.value = true;
    } else if (messages.value.length > 0) {
        // If the last message is from the user, we're waiting for an assistant response
        // This handles the case when starting a chat from homepage with initial_message
        const lastMessage = messages.value[messages.value.length - 1];
        if (lastMessage.role === "user" && !lastMessage.hidden) {
            is_waiting_for_initial_response.value = true;
            waiting_for_assistant.value = true;
        } else {
            is_waiting_for_initial_response.value = false;
            if (hasDevelopmentGoalDraft) {
                waiting_for_assistant.value = false;
            }
        }
    }

    // Store initialized state so that we don't reinitialize if we disconnect and reconnect
    isInitialized.value = true;
};

const handleTextboxShown = () => {
    has_taken_action.value = true;
};

const getMessageById = (message_id, temp_id = undefined) => {
    for (let m of messages.value) {
        // TODO: Clean up the keys here
        if (m.id == message_id || m.message_id == message_id || m.chat_message_id == message_id) {
            return m;
        }
        if (temp_id && (m.temp_id == temp_id || m.message_id == temp_id)) {
            return m;
        }
    }
    return false;
};

const getMessageLine = (message_id, lineIdx) => {
    const message = getMessageById(message_id);
    if (message) {
        return message.lines[lineIdx];
    }
    return false;
};

const handleChatMessage = ({ data }) => {
    if (data.uce_id && cancelledUceIds.value.has(data.uce_id)) {
        // If this is the final message for a cancelled exchange, clean up the cancelled ID
        if (data.role === "assistant") {
            cancelledUceIds.value.delete(data.uce_id);
        }
        return;
    }
    // Do whatever UI state needed, add to message queue
    // Clear the slow-response timer on every assistant chat_message. The old guard here
    // was `if (waiting_for_assistant.value)`, which is a vestige of a coarser whole-function
    // gate and failed in tool-call flows where an intermediate chat_message had already
    // flipped waiting_for_assistant=false — letting the last-armed partial timer survive
    // to fire under the finalized response. Gate on role so user-role re-emits don't touch
    // timer state they didn't own.
    if (data.role === "assistant") {
        clearSlowResponseTimeout();
    }

    // Cancel any pending RAF to prevent it from running after we set the final lines
    // This avoids a race condition where the RAF callback would access deleted streaming_content
    if (partialUpdateRafId) {
        cancelAnimationFrame(partialUpdateRafId);
        partialUpdateRafId = null;
    }

    const m = getMessageById(data.message_id, data.temp_id);

    if (m) {
        m.lines = data.lines;
        // Clean up streaming content now that we have the final parsed lines
        delete m.streaming_content;

        // re-fetches connections via inertia if the message has tagged a person
        if (messageHasActions(m, [ACTION.TAGGED_PERSON])) {
            router.reload({
                only: ["connections"],
                preserveState: true,
                preserveScroll: true,
            });
        }

        if (data.message_id) {
            m.id = data.message_id;
        }
        if (data.chat_message_id) {
            m.chat_message_id = data.chat_message_id;
        }
        if (data.chat_message_uuid) {
            m.chat_message_uuid = data.chat_message_uuid;
        }
        if (data.feedback_target !== undefined) {
            m.feedback_target = data.feedback_target;
        }
    } else if (data.temp_id && pendingSoftDeleteTempIds.value.has(data.temp_id)) {
        pendingSoftDeleteTempIds.value.delete(data.temp_id);
        if (data.chat_message_uuid) {
            fetchApi(`/v1/chat/messages/${data.chat_message_uuid}/edit`, { method: "POST" }).catch((err) => {
                logError(err, { context: { operation: "deferred_stop_soft_delete" } });
            });
        }
    } else {
        messages.value.push(data);
    }

    if (data.role === "assistant") {
        // A tool-call message is an intermediate assistant turn. Keep the exchange busy
        // while the tool card shows its own progress, then wait for the follow-up response.
        waiting_for_assistant.value = messageHasActions(data, [ACTION.TOOL_CALL]);
        // Reset thinking states when complete message arrives
        is_waiting_for_initial_response.value = false;
        is_thinking.value = false;

        logUserInteraction("coach_message_ended", { uce_id: data.uce_id }, cs_uuid);
        // note: rendered event used to be after message finished animating, we don't animate anymore so this has been moved here
        logUserInteraction("coach_message_rendered", { uce_id: data.uce_id }, cs_uuid);

        tagConnectionsOnAssistantMessage(m, data.chat_message_uuid);
    } else if (data.role === "tool" && data.uce_id) {
        // A goal draft is the terminal, actionable response for this turn, including when
        // the model emits no additional assistant content after its tool call.
        if (isDevelopmentGoalDraftMessage(data)) {
            resetAssistantResponseState();
        } else {
            // Tool results are always followed by another assistant turn. The completed tool
            // card is static, so bridge that otherwise blank interval with the normal loader.
            waiting_for_assistant.value = true;
            is_waiting_for_initial_response.value = true;
            is_thinking.value = false;
        }
    }
};

const tagConnectionsOnAssistantMessage = async (message, messageUuid) => {
    if (!memory_connections_frontend_driven_enabled) {
        return;
    }
    if (!message || !messageUuid) {
        return;
    }
    // Defensively suppress the slow-response banner during our known-good async call —
    // we don't want "gather my thoughts" appearing while labels pop in.
    clearSlowResponseTimeout();
    try {
        const response = await fetchApi(`/v1/chat/messages/${messageUuid}/evaluate-and-tag-connections`, { method: "POST" });
        if (!response.ok) {
            return;
        }
        const body = await response.json();
        if (!body?.actions?.length) {
            return;
        }

        message.lines = [...(message.lines ?? []), ...body.actions];

        router.reload({
            only: ["connections"],
            preserveState: true,
            preserveScroll: true,
        });
    } catch (err) {
        logError(err);
    }
};

const handleChatWarning = (data) => {
    chatError.value = data.data.message;
};

const handleChatError = (data) => {
    resetAssistantResponseState();
    chatError.value = data.data.message;
};

const handlePiiRejected = (data) => {
    // Remove the optimistic user message and restore its content to the input for editing
    const tempId = data.data?.temp_id;
    let rejectedContent = "";
    if (tempId) {
        const idx = messages.value.findIndex((m) => m.temp_id === tempId);
        if (idx !== -1) {
            rejectedContent = messages.value[idx].content || "";
            messages.value.splice(idx, 1);
        }
    }
    // Reset chat state based on rejection:
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    clearSlowResponseTimeout();
    chatError.value = data.data?.reason || "Your message was blocked because it contains personal information.";
    if (rejectedContent) {
        emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, rejectedContent);
    }
};

const handleChatPartialAddition = (data) => {
    if (data.uce_id && cancelledUceIds.value.has(data.uce_id)) {
        return;
    }
    waiting_for_assistant.value = true;
    clearSlowResponseTimeout();

    // Clear thinking states as soon as we start receiving visible content
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;

    const m = getMessageById(data.data["message_id"]);

    if (m) {
        // Delta mode: accumulate the delta content for better streaming performance
        if (data.data["delta_content"] !== undefined) {
            // Accumulate content immediately (no data loss)
            m.streaming_content = (m.streaming_content || "") + data.data["delta_content"];

            // Throttle reactive lines update to animation frames (~60fps max)
            // This reduces CPU overhead from excessive DOM updates during fast streaming
            if (!partialUpdateRafId) {
                partialUpdateRafId = requestAnimationFrame(() => {
                    partialUpdateRafId = null;
                    m.lines = [{ type: "md", content: m.streaming_content }];
                });
            }
        } else {
            // Legacy mode: full lines replacement (backwards compatibility)
            m.lines = data.data["lines"];
        }
    } else {
        const newMessage = { ...data.data };
        // Delta mode: initialize streaming_content from delta
        if (newMessage["delta_content"] !== undefined) {
            newMessage.streaming_content = newMessage["delta_content"];
            newMessage.lines = [{ type: "md", content: newMessage.streaming_content }];
            delete newMessage["delta_content"];
        }
        messages.value.push(newMessage);
        if (newMessage["role"] == "assistant") {
            logUserInteraction("coach_message_start", { uce_id: newMessage["uce_id"] }, cs_uuid);
        }
    }

    /*
     * We may receive many partial additions in quick succession,
     * often times faster than we can notice. We throttle the automatic
     * scrolling accordingly to avoid jitter.
     */
    startSlowResponseTimeout({ uce_id: data.uce_id });
};

const handleToolCallStatus = (data) => {
    const toolCallId = data.data["tool_call_id"];
    const statusText = data.data["status_text"];
    if (!toolCallId || !statusText) {
        return;
    }
    toolCallStatusText.set(toolCallId, statusText);
};

const handleTranscriptionEvent = (transcription) => {
    if (!waiting_for_transcript.value) {
        return;
    }

    waiting_for_transcript.value = false;
    show_slow_transcription.value = false;
    dictated_in_this_message.value = true;
    logUserInteraction("transcription_ended", { uce_id: userCoachExchangeId.value }, cs_uuid);
    sendMessage(transcription, {
        promptSwitchCallCode: promptSwitchCallCode.value,
        rolePlayAdjust: rolePlayAdjust.value,
        files: useChatStore.files,
        uce_id: userCoachExchangeId.value,
    });
    useChatStore.removeAllFiles();
    promptSwitchCallCode.value = null;
    rolePlayAdjust.value = null;
};

const updateActionState = (data) => {
    const { action_state, action_params, message_id, lineIdx, ...rest } = data;
    const existingLine = getMessageLine(message_id, lineIdx);
    const line = { ...existingLine };
    if (action_state) {
        line.action_state = {
            ...line.action_state,
            ...action_state,
        };
    }

    if (action_params) {
        line.action_params = {
            ...line.action_params,
            ...action_params,
        };
    }

    for (let x in rest) {
        line[x] = rest[x];
    }

    messages.value = messages.value.map((message) => {
        if (message_id === message.chat_message_id) {
            // First optimistic write for an action the server hasn't echoed back yet:
            // there is no line to patch, so append it (callers supply type/action_name).
            if (!existingLine && lineIdx === undefined && line.type === "action" && line.action_name) {
                return { ...message, lines: [...message.lines, line] };
            }
            return {
                ...message,
                lines: message.lines.map((l, idx) => {
                    if (idx === lineIdx) {
                        return line;
                    } else {
                        return l;
                    }
                }),
            };
        } else {
            return message;
        }
    });
};

provide("updateActionState", updateActionState);

const handleFollowupChange = (data) => {
    updateActionState(data);
    $sendEvent("action", { data: { type: ACTION.FOLLOW_UP, payload: data } });
};

const handleChooseFollowup = (data) => {
    updateActionState(data);
    $sendEvent("action", { data: { type: ACTION.FOLLOW_UP, payload: data } });
    markOnboardChecklistItemComplete(ONBOARDING_TASK.FOLLOWUP);
};

const handleRolePlayAdjust = (payload) => {
    if (payload.action_params?.adjustment) {
        rolePlayAdjust.value = payload.action_params.adjustment;
    }
    // type/action_name let updateActionState append the line when the server
    // hasn't echoed one back yet, so the widget's label reflects the click.
    updateActionState({ ...payload, type: "action", action_name: ACTION.ROLE_PLAY_ADJUST });
    // ActionHandler looks up ChatMessage by PK. Streaming rows use a timestamp
    // string, and temp user rows have no id yet — posting those 500s.
    if (typeof payload.message_id !== "number" || payload.message_id <= 0) {
        return;
    }
    $sendEvent("action", { data: { type: ACTION.ROLE_PLAY_ADJUST, payload } }).catch((error) => {
        console.error("Failed to save role play style", error);
        emitter.emit("error_notification", "Something went wrong saving your role play style. Please try selecting it again.");
    });
};

const handleProfileQuestion = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: "profile_question", payload } });
};

const handleActionItems = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: "action_items", payload } });
};

const handleVoiceNudge = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: "voice_nudge", payload } });
};

// Persists the PM picker's clicks (for the session debug view) and its collapsed state,
// so returning to the chat doesn't re-present the wizard for a choice the user already made.
const handlePmPicker = (payload) => {
    updateActionState(payload);
    $sendEvent("action", { data: { type: ACTION.PM_PICKER, payload } });
};

const msgBuffer = ref(null);
const isBufferingMessage = computed(() => !!msgBuffer.value || showStartLoadingMessage.value);

watch(waiting_for_assistant, (newVal) => {
    if (!newVal && msgBuffer.value != null) {
        sendBufferedMessage(msgBuffer.value);
        msgBuffer.value = null;
    }
});
const sendBufferedMessage = ({ content, files }) => {
    sendMessage(content, { promptSwitchCallCode: promptSwitchCallCode.value, rolePlayAdjust: rolePlayAdjust.value, files });
    promptSwitchCallCode.value = null;
    rolePlayAdjust.value = null;
};

const handleSendMessage = ({ content, files }) => {
    if (waiting_for_assistant.value) {
        msgBuffer.value = { content, files };
    } else {
        sendBufferedMessage({ content, files });
    }
};

// Track cancelled UCE IDs so late-arriving streaming events are ignored.
// Capped at 20 entries to prevent unbounded growth if the backend never
// sends a final assistant message for a cancelled exchange.
const MAX_CANCELLED_UCE_IDS = 20;
const cancelledUceIds = ref(new Set());
const pendingSoftDeleteTempIds = ref(new Set());

const handleStopGenerating = () => {
    // Evict oldest entry if at capacity
    if (cancelledUceIds.value.size >= MAX_CANCELLED_UCE_IDS) {
        const oldest = cancelledUceIds.value.values().next().value;
        cancelledUceIds.value.delete(oldest);
    }
    // Capture the current UCE ID before resetting so we can ignore late-arriving chunks
    cancelledUceIds.value.add(userCoachExchangeId.value);

    // Abort the in-flight HTTP streaming request so the server cancels the handler
    socketClient.value?.abortActiveRequest();

    // Remove the streaming assistant message (the partial response) if present
    const lastMsg = messages.value[messages.value.length - 1];
    if (lastMsg && lastMsg.role === "assistant" && !lastMsg.id) {
        messages.value.pop();
    }

    // Remove the user message and restore its text to the input editor
    const userMsg = messages.value[messages.value.length - 1];
    if (userMsg && userMsg.role === "user") {
        messages.value.pop();
        const mdLines = userMsg.lines?.filter((l) => l.type === "md") ?? [];
        const restoredContent = mdLines.map((l) => ("content" in l ? l.content : "")).join("\n");
        if (restoredContent) {
            emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, restoredContent);
        }
        // Soft-delete from backend so it doesn't reappear on refresh
        if (userMsg.chat_message_uuid) {
            fetchApi(`/v1/chat/messages/${userMsg.chat_message_uuid}/edit`, { method: "POST" }).catch((err) => {
                logError(err, { context: { operation: "stop_generating_soft_delete" } });
            });
        } else if (userMsg.temp_id) {
            pendingSoftDeleteTempIds.value.add(userMsg.temp_id);
        }
    }

    // Reset all waiting/thinking state
    waiting_for_assistant.value = false;
    is_waiting_for_initial_response.value = false;
    is_thinking.value = false;
    show_slow_response.value = false;
    clearSlowResponseTimeout();
    msgBuffer.value = null;

    logUserInteraction("stop_generating", { uce_id: userCoachExchangeId.value }, cs_uuid);
};

const handleEditMessage = ({ messageIdx, content, chatMessageUuid }) => {
    // If currently generating, stop first — it already removes messages,
    // restores text, and soft-deletes, so we can return early.
    if (waiting_for_assistant.value) {
        handleStopGenerating();
        return;
    }

    // Remove the edited message and all subsequent messages from the frontend
    messages.value.splice(messageIdx);

    // Ensure the composer is visible (it may be hidden behind "Start New Session" controls)
    returnToChatChoiceMade.value = true;

    // Restore the message text to the input box and scroll down
    if (content) {
        emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, content);
        scrollToBottom();
    }

    // Soft-delete the messages on the backend so they don't reappear on refresh
    if (chatMessageUuid) {
        fetchApi(`/v1/chat/messages/${chatMessageUuid}/edit`, { method: "POST" }).catch((err) => {
            logError(err, { context: { operation: "edit_message_soft_delete" } });
        });
    }

    logUserInteraction("edit_message", { message_idx: messageIdx }, cs_uuid);
};

const handleOnboardingFollowup = () => {
    markOnboardChecklistItemComplete(ONBOARDING_TASK.FOLLOWUP);
};

const handleWrapUpChat = () => {
    logUserInteraction(CHAT_EVENT.WRAP_UP_CHAT, {}, cs_uuid);
    isChatWrapUpDialogOpen.value = true;
};

const handleCloseChatWrapUpDialog = () => {
    isChatWrapUpDialogOpen.value = false;
};

const handleOpenEmailSummary = () => {
    logUserInteraction(CHAT_EVENT.OPEN_SESSION_SUMMARY, {}, cs_uuid);
    isEmailSummaryDialogOpen.value = true;
};

/**
 * Send a chat message to the backend via WebSocket
 * @param {string} content - The message content to send
 * @param {Object} options - Optional parameters
 * @param {string} [options.promptSwitchCallCode] - Code to switch prompts (e.g., "Role_Play", "triage")
 * @param {string} [options.rolePlayAdjust] - Adjustment parameter for ongoing role play
 * @param {Array} [options.files] - Array of file objects to attach to the message
 * @param {Object|false} [options.rolePlayContext] - Role play context with name, relationship, personality, context fields.
 *                                                    Pass false to explicitly dismiss role play context.
 * @param {string} [options.uce_id] - User coach exchange ID for tracking
 */
const sendMessage = (content, { promptSwitchCallCode, rolePlayAdjust, files = [], rolePlayContext, uce_id } = {}) => {
    if (isDevelopmentGoalChoicePending.value) {
        return;
    }

    uce_id = uce_id ?? cycleUserCoachExchangeId();
    if (content.length > 0 || files.length > 0) {
        waiting_for_assistant.value = true;
        is_waiting_for_initial_response.value = true;
        has_taken_action.value = true;
        // Temporary message so the backend can send back data about this message
        const temp_id = `${Date.now()}${cs_id}`;
        const message = {
            temp_id: temp_id,
            uce_id,
            content,
            role: "user",
            dictation_on: useChatStore.dictationOn,
            use_workiq: useChatStore.useWorkiq,
            dictated: dictated_in_this_message.value,
            files,
        };
        if (promptSwitchCallCode) {
            message.prompt_switch_call_code = promptSwitchCallCode;
        }
        if (rolePlayAdjust) {
            message.role_play_adjust = rolePlayAdjust;
        }
        if (rolePlayContext) {
            message.role_play_context = rolePlayContext;
        }
        $sendSocketEvent("user_chat_message", message, socketClient.value);

        // calculates diff in last message and current, if greater than 24 hours sends an additional property in the event
        const RETURN_SESSION_HOURS = 24;
        const lastMessage = messages.value[messages.value.length - 1];
        const lastMessageDateTime = lastMessage?.created_at ? convertToDateTime(lastMessage.created_at) : undefined;
        const differenceInHours = Math.abs(lastMessageDateTime?.diffNow("hours").hours ?? 0);

        logUserInteraction(
            AmplitudeEventNames.USER_MESSAGE_SENT,
            {
                uce_id,
                mic: dictated_in_this_message.value,
                coaching_session_id: cs_uuid,
                chat_source,
                ...(differenceInHours > RETURN_SESSION_HOURS && { is_return_session: true }),
            },
            cs_uuid,
        );

        message.lines = [{ type: "md", content }];
        // We don't want to show the role play message
        if (!rolePlayContext) {
            messages.value.push(message);
        }
        scrollToBottom();

        dictated_in_this_message.value = false;

        const userMessagesCount = messages.value.filter((message) => message.role === "user").length;
        if (userMessagesCount === 3) {
            markOnboardChecklistItemComplete(ONBOARDING_TASK.GUIDANCE);
        }

        startSlowResponseTimeout({ timeout_ms: 4000, uce_id });
    }
};
provide("sendMessage", sendMessage);

const keepEditingDevelopmentGoal = (message, lineIdx) => {
    const line = message.lines[lineIdx];
    if (line !== latestDevelopmentGoalAction.value?.line || waiting_for_assistant.value) {
        return;
    }

    logDevelopmentGoalCardAction(DEVELOPMENT_GOAL_CARD_ACTION.KEEP_EDITING);
    dismissDevelopmentGoalLockIn(message, lineIdx);
};

const handleKeyDown = () => {
    if (chatError.value) {
        chatError.value = null;
    }
};

const handleTranscribeRecording = async ({ blob, recordingTime, audioStats }) => {
    const eventData = {
        transcribe_session_start: Date.now(),
        blob_size: blob.size,
        blob_type: blob.type,
        recording_time: recordingTime,
        user_agent: navigator.userAgent,
        uce_id: cycleUserCoachExchangeId(),
        ...(audioStats && {
            audio_audible_pct: Math.round(audioStats.audiblePercentage * 1000) / 1000,
            audio_avg_level: Math.round(audioStats.avgLevel * 1000) / 1000,
            audio_peak_level: Math.round(audioStats.peakLevel * 1000) / 1000,
            audio_sample_count: audioStats.sampleCount,
            ...(audioStats.warmupMs != null && { audio_warmup_ms: audioStats.warmupMs }),
            ...(audioStats.initToStartMs != null && { init_to_start_ms: audioStats.initToStartMs }),
        }),
    };

    waiting_for_transcript.value = true;

    // Demoing feature!
    // Check if we have any remaining items to go through in our dev tools script
    if (devtoolsEnabled.value) {
        const chatScriptStorageKey = `chat_script_${first_prompt_id}`;
        const script = JSON.parse(window.localStorage.getItem(chatScriptStorageKey, "{}"));
        const messages = script?.messages || [];

        const messagesExist = messages.some((message) => message.message.length > 0);

        // Script index needs to be unique to the coaching session
        const scriptIndexKey = `chat_script_current_index_${cs_id}`;
        const currentScriptIndex = Number(window.localStorage.getItem(scriptIndexKey, 0));

        if (messagesExist && currentScriptIndex < messages.length) {
            const currentMessage = messages[currentScriptIndex].message;
            window.localStorage.setItem(scriptIndexKey, currentScriptIndex + 1);

            // Wait between 300 and 700ms to make it look like we're actually doing some work here
            setTimeout(
                () => {
                    sendMessage(currentMessage);
                    waiting_for_transcript.value = false;
                },
                getRandomInt(300, 700),
            );

            return;
        }
    }

    try {
        const transcript = await transcribeRecording({
            blob,
            onStart: () => {
                show_slow_transcription.value = false;
                logUserInteraction("transcription_request_start", eventData, cs_uuid);
            },
            onSlow: () => {
                show_slow_transcription.value = true;
                logUserInteraction("saw_slow_transcription_message", eventData, cs_uuid);
            },
            onError: (err) => {
                logUserInteraction("saw_transcription_error_message", eventData, cs_uuid);
                logUserInteraction("transcription_request_failed", eventData, cs_uuid);
                logError(err, {
                    context: { transcription: { cs_id, source: "ChatDetail" } },
                    tags: { error_type: "transcription", error_code: err.error_code },
                });
            },
        });
        logUserInteraction("transcription_request_succeeded", eventData, cs_uuid);
        handleTranscriptionEvent(transcript);
    } catch (err) {
        if (err.error_code === "no_speech_detected") {
            chatError.value = CHAT_ERROR.NO_SPEECH_DETECTED;
            logUserInteraction("no_speech_detected", eventData, cs_uuid);
        } else {
            chatError.value = CHAT_ERROR.TRANSCRIPTION_EXHAUSTED;
            logUserInteraction("transcription_request_retries_exhausted", eventData, cs_uuid);
        }
    } finally {
        waiting_for_transcript.value = false;
        show_slow_transcription.value = false;
    }
};

const handleRecordingComplete = (recording) => {
    markOnboardChecklistItemComplete(ONBOARDING_TASK.MIC);
    handleTranscribeRecording(recording);
};

const handleRecordingError = () => {
    chatError.value = CHAT_ERROR.RECORDING_DEFAULT;
};

const handleRecordingStart = () => {
    chatError.value = null;
    has_taken_action.value = true;
};

const chatMessageListRef = useTemplateRef("chatMessageList");

const scrollToBottom = () => {
    /*
     * Delegate scroll to the next tick as this function
     * is invoked at the same time that messages state is mutated.
     * Our scrolling logic depends on DOM elements, so we make sure
     * that this runs when Vue has reconciled the DOM state.
     */
    nextTick(() => {
        if (chatMessageListRef.value) {
            chatMessageListRef.value.scrollToLastMessage();
        }
    });
};

const startSlowResponseTimeout = ({ timeout_ms, ...event_data } = {}) => {
    clearSlowResponseTimeout();

    show_slow_response.value = false;
    slow_response_timeout.value = setTimeout(() => {
        startFailedResponseTimeout(event_data);
        show_slow_response.value = true;
        logUserInteraction("saw_slow_message", event_data, cs_uuid);
    }, timeout_ms ?? SLOW_MESSAGE_TIMEOUT_MS);
};

const clearSlowResponseTimeout = () => {
    clearFailedResponseTimeout();
    show_slow_response.value = false;
    clearTimeout(slow_response_timeout.value);
};

const startFailedResponseTimeout = ({ timeout_ms, ...event_data } = {}) => {
    clearFailedResponseTimeout();

    failed_response_timeout.value = setTimeout(() => {
        show_failed_response.value = true;
        logUserInteraction("saw_failed_message", event_data, cs_uuid);
    }, timeout_ms ?? FAILED_MESSAGE_TIMEOUT_MS);
};

const clearFailedResponseTimeout = () => {
    show_failed_response.value = false;
    clearTimeout(failed_response_timeout.value);
};

const markOnboardChecklistItemComplete = (taskId) => {
    emitter.emit(CHAT_EVENT.MARK_ONBOARD_CHECKLIST, { taskId });
};

const handleToggleWidgetSidebar = () => {
    showWidgetSidebar.value = !showWidgetSidebar.value;
};

const handleFailedResponseReload = () => {
    document.location.reload();
};

const handleRolePlayStarted = (payload) => {
    useChatStore.setRolePlay(payload.data);
};

const handleEndRolePlay = () => {
    if (useChatStore.rolePlay?.hashid) {
        router.put(
            `/role-play/summary/${useChatStore.rolePlay.hashid}/`,
            {},
            {
                headers: {
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                onSuccess: () => {
                    logUserInteraction("role_play_summary_sent", { id: useChatStore.rolePlay.id }, cs_uuid);
                    useChatStore.setRolePlay({});
                },
                onError: () => {
                    chatError.value = "Failed to end role play and generate feedback. Please try again.";
                    logError(`Failed to navigate to role play summary: ${useChatStore.rolePlay.id}`);
                },
            },
        );
    }
};

/**
 * Handle starting a role play session from within the chat
 * @param {Object} options
 * @param {Object} options.rolePlayContext - Role play setup context containing:
 * @param {string} options.rolePlayContext.name - Name of the person being role-played
 * @param {string} options.rolePlayContext.relationship - Relationship to the user (e.g., "My manager")
 * @param {string} options.rolePlayContext.personality - Personality traits of the person
 * @param {string} options.rolePlayContext.context - Scenario/situation context for the role play
 */
const handleInChatRolePlayStarted = ({ rolePlayContext }) => {
    const rolePlayMessage = "I want to role play this scenario.";
    sendMessage(rolePlayMessage, { rolePlayContext, files: useChatStore.files });
};

/**
 * Handle canceling an ongoing role play session
 * Sends a message with rolePlayContext: false to signal cancellation (gets converted to false/None on backend)
 */
const handleInChatRolePlayCanceled = () => {
    const rolePlayCanceledMessage = "I don't want to role play anymore. Let's discuss something else.";
    sendMessage(rolePlayCanceledMessage, { rolePlayContext: false, files: useChatStore.files });
};

const handleErrorNotification = (message) => {
    chatError.value = message;
    chatSuccess.value = null; // Clear success message when showing error
};

const handleSuccessNotification = (message) => {
    chatSuccess.value = message;
    chatError.value = null; // Clear error message when showing success
};

const handleInitialResponseStarted = () => {
    is_waiting_for_initial_response.value = false;
};

const handleChatSendDeduplicated = () => {
    is_thinking.value = false;
    is_waiting_for_initial_response.value = false;
};

const handleThinkingStarted = () => {
    is_thinking.value = true;
};

const handleVisibleContentStarted = () => {
    is_thinking.value = false;
};

const handleContinueChat = () => {
    returnToChatChoiceMade.value = true;
};

const handleGoBack = () => {
    if (is_development_journeys_chat && development_journeys_url) {
        returnToDevelopmentJourneys();
        return;
    }

    if (globalThis.history && globalThis.history.length > 1) {
        globalThis.history.back();
    } else {
        router.visit("/coach/history");
    }
};

// After the keyboard finishes animating in and the layout has reflowed,
// anchor the latest assistant message below the top fade. One animation
// frame lets the new `clientHeight` become observable before scrolling.
const { settledKeyboardHeight } = useKeyboard();
watch(settledKeyboardHeight, (next) => {
    if (next > 0) {
        requestAnimationFrame(() => {
            chatMessageListRef.value?.pinLatestAssistantToTop();
        });
    }
});
</script>
